import { IUndoRedoElement, BaseAction } from '../../undo/common/action.js';
import { IInstantiationService, ServicesAccessor } from '../../instantiation/common/instantiation.js';
import { createDecorator } from '../../instantiation/common/registry.js';
import { Emitter, Event } from '../../../base/common/events.js';
import { Disposable, IDisposable, toDisposable } from '../../../base/common/lifecycle.js';
import { IJSONSchema } from '../../../base/common/jsonSchema.js';
import { ILocalizedString } from '../../action/common/action.js';
import { Iterable } from '../../../base/common/iterator.js';
import { LinkedList } from '../../../base/common/linkedList.js';
import { TypeConstraint, validateConstraints } from '../../../base/common/types.js';
import { StandardKeyboardEvent } from '../../../base/browser/keyboardEvent.js';



/**
 * Service identifier for dependency injection
 */
export const ICommandService = createDecorator<ICommandService>('commandService');

export interface ICommandEvent {
	readonly commandId: string;
	readonly args: unknown[];
}


export interface ICommandContextUser {
	isAi: false,
	keyboardEvent?: StandardKeyboardEvent,
};
export interface ICommandContextAI {
	isAi: true,
	keyboardEvent?: undefined,
};
export type ICommandContext = ICommandContextUser | ICommandContextAI;

export interface ExecuteCommandResult<TResult> {
	success: boolean;
	message: string;
	result: TResult;
};
export type AICommandResult = string | number | boolean;

/**
 * Service for registering and executing commands.
 * All operations should go through commands for consistency,
 * undo/redo support, and extensibility.
 */
export interface ICommandService {

	readonly onWillExecuteCommand: Event<ICommandEvent>;
	readonly onDidExecuteCommand: Event<ICommandEvent>;

	readonly onDidChange: Event<CommandServiceDidChangeEvent>;

	/**
	 * Execute a command with type inference from the command class
	 * @example executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, { property, value })
	 */
	executeCommand<TCommand extends ICommand<any, any> = ICommand<unknown, unknown>>(
		id: string,
		args: TCommand extends ICommand<infer TArgs, any> ? TArgs : unknown,
		context?: ICommandContextUser
	): Promise<ExecuteCommandResult<TCommand extends ICommand<any, infer TResult> ? TResult : unknown>>;

	/**
	 * Execute a command for AI context
	 */
	executeCommand(
		id: string,
		args: unknown,
		context: ICommandContextAI
	): Promise<ExecuteCommandResult<AICommandResult>>;

	/**
	 * Check if a command can be executed
	 */
	canExecuteCommand(id: string, args: unknown): ICommandCanExecuteReturnValue;
}


export type ICommandsMap = Map<string, ICommand>;

export interface ICommandCanExecuteReturnValue {
	canExecute: boolean;
	reason?: string;
	code?: string;
}


export interface ICommandHandler<TArgs extends unknown = unknown, TResult extends unknown | Promise<unknown> = unknown> {
	(accessor: ServicesAccessor, args: TArgs, context?: ICommandContext): TResult;
}

/**
 * Represents a command that can be executed.
 * Commands are the primary way to perform operations in the editor.
 */
export interface ICommand<
	TArgs extends unknown = unknown,
	TResult extends unknown | Promise<unknown> = unknown
> {
	/**
	 * Unique identifier for this command
	 */
	readonly id: string;

	/**
	 * Human-readable label for this command
	 */
	readonly label?: string;

	readonly metadata?: ICommandMetadata | null;

	/**
	 * Check if the command can be executed with the given arguments
	 */
	canExecute?(arg: TArgs): ICommandCanExecuteReturnValue;

	/**
	 * Execute the command with the given arguments
	 */
	execute: ICommandHandler<TArgs, TResult>;

	/**
	 * Optional: Execute the command for AI tools.
	 * If implemented, this command can be used by AI tools and must return a string response.
	 * If not implemented, the command cannot be used by AI tools.
	 */
	executeForAI?(accessor: ServicesAccessor, args: TArgs, context?: ICommandContextAI): Promise<AICommandResult> | AICommandResult;
}

export interface ICommandMetadata {
	/**
	 * NOTE: Please use an ILocalizedString. string is in the type for backcompat for now.
	 * A short summary of what the command does. This will be used in:
	 * - API commands
	 * - when showing keybindings that have no other UX
	 * - when searching for commands in the Command Palette
	 */
	readonly description: ILocalizedString | string;
	readonly args?: {
		readonly name: string;
		readonly isOptional?: boolean;
		readonly description?: string;
		// readonly constraint?: TypeConstraint;
		readonly schema?: IJSONSchema;
		readonly allowMcp?: boolean;
	};
	readonly returns?: string;
}



export interface ICommandRegistry {
	readonly onDidRegisterCommand: Event<string>;
	registerCommand(id: string, command: ICommandHandler): IDisposable;
	registerCommand(command: ICommand): IDisposable;
	registerCommandAlias(oldId: string, newId: string): IDisposable;
	getCommand(id: string): ICommand | undefined;
	getCommands(): ICommandsMap;
}

export const CommandsRegistry: ICommandRegistry = new class implements ICommandRegistry {

	private readonly _commands = new Map<string, LinkedList<ICommand>>();

	private readonly _onDidRegisterCommand = new Emitter<string>();
	readonly onDidRegisterCommand: Event<string> = this._onDidRegisterCommand.event;

	registerCommand(idOrCommand: string | ICommand, handler?: ICommandHandler): IDisposable {

		if (!idOrCommand) {
			throw new Error(`invalid command`);
		}

		if (typeof idOrCommand === 'string') {
			if (!handler) {
				throw new Error(`invalid command`);
			}
			return this.registerCommand({ id: idOrCommand, execute: handler });
		}

		// add argument validation if rich command metadata is provided
		if (idOrCommand.metadata && Array.isArray(idOrCommand.metadata.args)) {
			const constraints: Array<TypeConstraint | undefined> = [];
			for (const arg of idOrCommand.metadata.args) {
				constraints.push(arg.constraint);
			}
			const actualHandler = idOrCommand.execute || /** compat for old naming (handler) */ (idOrCommand as unknown as { handler: ICommandHandler }).handler;
			idOrCommand.execute = function (accessor, args) {
				validateConstraints([args], constraints);
				return actualHandler(accessor, args);
			};
		}

		// find a place to store the command
		const { id } = idOrCommand;

		let commands = this._commands.get(id);
		if (!commands) {
			commands = new LinkedList<ICommand>();
			this._commands.set(id, commands);
		}

		const removeFn = commands.unshift(idOrCommand);

		const ret = toDisposable(() => {
			removeFn();
			const command = this._commands.get(id);
			if (command?.isEmpty()) {
				this._commands.delete(id);
			}
		});

		// tell the world about this command
		this._onDidRegisterCommand.fire(id);

		// return markAsSingleton(ret);
		return ret;
	}

	registerCommandAlias(oldId: string, newId: string): IDisposable {
		return CommandsRegistry.registerCommand(oldId, (accessor, args) => accessor.get(ICommandService).executeCommand(newId, args));
	}

	getCommand(id: string): ICommand | undefined {
		const list = this._commands.get(id);
		if (!list || list.isEmpty()) {
			return undefined;
		}
		return Iterable.first(list);
	}

	getCommands(): ICommandsMap {
		const result = new Map<string, ICommand>();
		for (const key of this._commands.keys()) {
			const command = this.getCommand(key);
			if (command) {
				result.set(key, command);
			}
		}
		return result;
	}
};

CommandsRegistry.registerCommand('noop', () => { });





// #region Undoable Command

/**
 * A command that can be undone.
 * These commands automatically create undo/redo actions.
 */
export interface IUndoableCommand<TArgs extends unknown = unknown, TResult extends unknown = unknown> extends ICommand<TArgs, TResult> {
	/**
	 * Create an undo/redo action for this command execution
	 */
	toAction(service: IInstantiationService, args: TArgs, context?: ICommandContext): IUndoRedoElement;
}
export function isUndoableCommand(command: ICommand): command is IUndoableCommand {
	return 'toAction' in command;
}

export interface CommandServiceDidChangeEvent {
	command: ICommand | null;
	type: 'registered' | 'unregistered';
}


export abstract class Command<TExecuteArgs extends unknown = unknown, TResult extends unknown = unknown> extends Disposable implements ICommand<TExecuteArgs, TResult> {

	constructor(
		public readonly id: string,
		public readonly label: string,
		public readonly metadata: ICommandMetadata | null = null
	) {
		super();
	}

	/**
	 * Validate if the command can execute with given arguments
	 */
	canExecute(_args: TExecuteArgs): ICommandCanExecuteReturnValue {
		return { canExecute: true };
	}

	abstract execute(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult;
}


/**
 * Base class for simple undoable commands.
 * Automatically creates undo/redo actions.
 */
export abstract class UndoableCommand<TExecuteArgs extends unknown = unknown, TResult extends unknown = unknown> implements IUndoableCommand<TExecuteArgs, TResult> {
	constructor(
		public readonly id: string,
		public readonly label: string,
		public readonly metadata: ICommandMetadata | null = null
	) {

	}

	/**
	 * Validate if the command can execute with given arguments
	 */
	abstract canExecute(args: TExecuteArgs): ICommandCanExecuteReturnValue;

	/**
	 * Perform the command (do the operation)
	 */
	protected abstract perform(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult;

	/**
	 * Undo the command (reverse the operation)
	 */
	protected abstract undo(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): void;

	/**
	 * Execute the command (calls perform)
	 */
	execute(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): TResult {
		return this.perform(accessor, args);
	}

	executeForAI?(accessor: ServicesAccessor, args: TExecuteArgs, context?: ICommandContext): AICommandResult | Promise<AICommandResult>;
	/**
	 * Create an undo/redo action for this command
	 */
	toAction(service: IInstantiationService, args: TExecuteArgs, context?: ICommandContext): IUndoRedoElement {
		// We need to clone the command instance to preserve state between undo/redo
		const clone = Object.create(this);
		Object.assign(clone, this);

		context = context ? { ...context } : context;

		const undoAction = new BaseAction({
			label: this.label,
			perform: () => service.invokeFunction(accessor => clone.execute(accessor, args, context)),
			undo: () => service.invokeFunction(accessor => clone.undo(accessor, args, context))
		});
		return undoAction;
	}
}
